import { ErrorSidePanel } from '@/components/errors';
import { type Profile, type SecurityCode } from '@/portal-api.definition';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  logger,
  ModuleName,
  Screen,
  templates,
  utils,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import z from 'zod';
import { kidProfileSchema } from '../../-model/kid-profile.schema';
import { profileFormLoaderFn, type ProfileForm } from '../../-model/profile-form';
import { skinConfig as upsertskinConfig } from '../../create';
import { type CreateProfileProps } from '../../create/-model/model';
import { useGetProfile } from './-hooks/profile.hook';
import { useDeleteProfile } from './-hooks/delete-profile.hook';
import { useUpdateProfile } from './-hooks/update-profile.hook';
import { type UpdateProfileProps } from './-model/props';
import {
  TvProfileUpdateLightLoading,
  TvProfileUpdateWooDarkLoading,
  TvProfileUpdateWooLoading,
} from './-skins';

export const Route = createFileRoute(
  '/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/$profileId/update/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: profileFormLoaderFn,
  staticData: {
    module: ModuleName.Tv,
    screen: Screen.EditProfile,
  },
  validateSearch: kidProfileSchema.extend({
    hasPin: z.preprocess((val) => val === 'true' || val === true, z.boolean()).default(false),
  }),
});

function RouteComponent() {
  const { id, profileId } = Route.useParams();
  const valueFromHash = Route.useLoaderData();
  const location = useLocation();

  const { kids, hasPin } = Route.useSearch();
  const { data: profile, isLoadingData } = useGetProfile(id, profileId);

  const [validation] = useState<{
    type: 'pin' | 'contact';
    pin?: string;
    securityCode?: SecurityCode;
  } | null>(() => {
    const state = location.state as { securityCode?: SecurityCode; pin?: string } | undefined;
    if (state?.securityCode) {
      return { type: 'contact', securityCode: state.securityCode };
    }
    if (state?.pin) {
      return { type: 'pin', pin: state.pin };
    }
    return null;
  });

  if (isLoadingData)
    return templates.getSkinSelector(
      skinConfig({
        isKidsProfile: kids,
        hasPin,
      }),
    );

  if (!profile) throw redirect({ to: '/tv/$id/profiles', params: { id } });

  const profileHasPin = !!profile.Pin;

  if (profileHasPin && !validation) {
    return <RedirectToPinValidation id={id} profileId={profileId} />;
  }

  const initialValue: Omit<ProfileForm, 'type'> = valueFromHash ?? {
    name: profile.Nickname,
    ageRating: profile.ContentRatingLimit,
    avatar: profile.Avatar ?? null,
    hasPin: !!profile.Pin,
    pin: profile.Pin ?? null,
    confirmPin: profile.Pin ?? null,
  };

  return (
    <EditProfileRouteComponent
      initialValue={initialValue}
      profile={profile}
      validation={validation}
    />
  );
}

function RedirectToPinValidation({ id, profileId }: Readonly<{ id: string; profileId: string }>) {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({
      to: '/tv/$id/profiles/$profileId/update/pin-validation',
      params: { id, profileId },
    });
  }, [navigate, id, profileId]);
  return null;
}

function skinConfig(props: UpdateProfileProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <TvProfileUpdateWooDarkLoading {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <TvProfileUpdateWooLoading {...props} />,
      },
    ],
    defaultValue: <TvProfileUpdateLightLoading {...props} />,
  };
}

function EditProfileRouteComponent({
  initialValue,
  profile,
  validation,
}: Readonly<{
  initialValue: Omit<ProfileForm, 'type'>;
  profile: Profile;
  validation: { type: 'pin' | 'contact'; pin?: string; securityCode?: SecurityCode } | null;
}>) {
  const { hash } = useLocation();
  const { id, profileId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { mutateAsync } = useUpdateProfile();
  const { mutateAsync: deleteProfile, isPending: isDeletingProfile } = useDeleteProfile();
  const form = useForm<ProfileForm>(
    {
      type: {
        value: profile.Type,
      },
      hasPin: {
        value: initialValue?.hasPin ?? false,
      },
      pin: {
        value: initialValue?.pin ?? '',
        validator: ({ hasPin }) =>
          hasPin
            ? z.string().min(widgets.PinCodeLength, 'TvProfiles.CreateProfile.Errors.PinRequired')
            : z.any(),
      },
      confirmPin: {
        value: initialValue?.confirmPin ?? '',
        validator: ({ hasPin, pin }) =>
          hasPin && pin && pin.length === widgets.PinCodeLength
            ? z.string().refine((val) => val === pin, 'TvProfiles.CreateProfile.Errors.PinMismatch')
            : z.any(),
      },
      name: {
        value: initialValue?.name ?? '',
        validator: z.string().min(1, 'TvProfiles.CreateProfile.Errors.NameRequired'),
      },
      ageRating: {
        value:
          initialValue?.ageRating ??
          (profile.Type === 'Kids' ? widgets.AgeRatings[0].TopSegmentKey : 'Todos'),
      },
      avatar: {
        value: initialValue?.avatar ?? null,
        validator: z.object(
          {
            Id: z.string(),
            ImageId: z.string(),
            ImageUri: z.union([z.string(), z.undefined()]),
          },
          { error: 'TvProfiles.CreateProfile.Errors.AvatarRequired' },
        ),
      },
    },
    {
      focusOnError: true,
      onError: logger.logFormErrors.bind(logger),
    },
  );

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!form.value) return;
      const compressed = await utils.compressToBase64Url(JSON.stringify(form.value));
      navigate({
        hash: compressed,
        replace: true,
        search: { kids: profile.Type === 'Kids', hasPin: !!profile.Pin },
        state: ((prev: Record<string, unknown>) => ({
          ...prev,
          pin: validation?.pin,
          securityCode: validation?.securityCode,
        })) as never,
      });
    }, 250);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.value)]);

  const props: CreateProfileProps = {
    isEditing: true,
    isKidsProfile: profile.Type === 'Kids',
    onSubmit: async (v) => {
      await mutateAsync({
        profile,
        newValues: v,
        sa: id,
        profilePin: validation?.type === 'pin' ? validation.pin : undefined,
        securityCode: validation?.type === 'contact' ? validation.securityCode : undefined,
      });
      navigate({ to: '/tv/$id/profiles', params: { id } });
    },
    formValueHash: hash,
    form,
    onDeleteProfile: async () => {
      await deleteProfile({ sa: id, profileId });
      navigate({ to: '/tv/$id/profiles', params: { id } });
    },
    isDeletingProfile,
    sa: id,
    profileId,
    hasPin: !!profile.Pin,
    validationState: validation
      ? { pin: validation.pin, securityCode: validation.securityCode }
      : undefined,
  };
  return templates.getSkinSelector(upsertskinConfig(props));
}
